import { useForm, zodResolver } from '@mantine/form'
import { Button, ButtonWithLoader } from '@neo/index'
import { Avatar, AvatarFallback } from '@neo/primitives/avatar'
import { Checkbox } from '@neo/primitives/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@neo/primitives/dialog'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Typography } from '@neo/primitives/typography'
import { getInitials } from '@shared/utils'
import { titleize } from 'inflection'
import { Share2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useUsersQuery } from 'v2/pages/settings/queries'
import { useAuth } from 'v2/shared/hooks/use-auth'
import type { RAciBridgeAccessRecords, RExternalOrgApp, RUsersList } from 'v2/types/api/settings'
import z from 'zod'
import {
  useAccessRecordsQuery,
  useGrantAccessMutation,
  useRevokeAccessMutation,
} from '../../queries'
import { getAccountIdentifier } from '../../utils/account-identifier'
import { AgentSelect } from './agents-select'

const schema = z.object({
  users_with_access: z.array(z.string()),
  share_with_organization: z.boolean().optional().default(false),
})

// Utility to get initials with fallback to email
const getUserInitials = (name: string, email: string): string => {
  const trimmedName = name?.trim()
  if (trimmedName) {
    return getInitials(trimmedName)
  }
  const trimmedEmail = email?.trim()
  if (trimmedEmail) {
    return trimmedEmail.substring(0, 2).toUpperCase()
  }
  return '?'
}

interface ShareConnectionButtonProps {
  app: RExternalOrgApp
  className?: string
}

type GrantAccessPayload = Record<string, unknown> & {
  users: string[]
  share_with_organization: boolean
}

type RevokeAccessPayload = Record<string, unknown> & {
  user?: string
  org?: boolean
}

interface ShareConnectionButtonViewProps extends ShareConnectionButtonProps {
  currentUserId?: string
  accessRecords?: RAciBridgeAccessRecords
  users?: RUsersList
  isLoadingAccessRecords: boolean
  isGrantAccessPending: boolean
  onGrant: (payload: GrantAccessPayload) => undefined | Promise<unknown>
  onRevoke: (payload: RevokeAccessPayload) => undefined | Promise<unknown>
  onDialogOpenChange?: (open: boolean) => void
}

export const ShareConnectionButtonView = ({
  app,
  className,
  currentUserId,
  accessRecords,
  users,
  isLoadingAccessRecords,
  isGrantAccessPending,
  onGrant,
  onRevoke,
  onDialogOpenChange,
}: ShareConnectionButtonViewProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const userMap = useMemo(() => {
    const m: Record<string, { name: string; email: string }> = {}
    for (const u of users?.members_list ?? []) {
      m[u.aom_uuid] = { name: u.account_name, email: u.email }
    }
    return m
  }, [users])

  // Check if current user is the one who integrated the app
  const ownerId = accessRecords?.owner || app.integrated_by?.id
  const isCurrentUserIntegrator = currentUserId === ownerId

  const form = useForm<{ users_with_access: string[]; share_with_organization: boolean }>({
    validate: zodResolver(schema),
    initialValues: {
      users_with_access: [],
      share_with_organization: false,
    },
  })

  useEffect(() => {
    // `org` is absent on the legacy source, which reports users only. Seed the
    // checkbox unchecked there rather than handing the form `undefined`.
    if (isModalOpen && accessRecords) {
      form.setFieldValue('share_with_organization', accessRecords.org ?? false)
    }
  }, [isModalOpen, accessRecords?.org])

  const handleShare = ({
    users_with_access,
    share_with_organization,
  }: {
    users_with_access: string[]
    share_with_organization: boolean
  }) => {
    if (!app.id) return

    const payload: GrantAccessPayload = {
      users: share_with_organization ? [] : users_with_access,
      share_with_organization: share_with_organization || Boolean(accessRecords?.org),
    }

    void Promise.resolve(onGrant(payload))
      .then(() => {
        form.reset()
        setIsModalOpen(false)
      })
      .catch(() => undefined)
  }

  const handleRevokeAccess = (userId: string) => {
    if (!app.id) return

    const payload = {
      user: userId,
    }

    void onRevoke(payload)
  }

  const handleRevokeOrganizationAccess = () => {
    if (!app.id) return
    void onRevoke({ org: false })
  }

  // Create excludeIds array including all users who already have access
  const excludeIds = [...(ownerId ? [ownerId] : []), ...(accessRecords?.users ?? [])]

  // Check if any users have been selected (form starts empty)
  const hasNewUsers = form.values.users_with_access.length > 0

  const onOpenChange = (open: boolean) => {
    setIsModalOpen(open)
    onDialogOpenChange?.(open)
    if (!open) {
      form.reset()
    }
  }

  // Only render the button for the user who integrated the app
  if (!isCurrentUserIntegrator) {
    return null
  }

  return (
    <>
      <Button
        variant="ghost"
        tabIndex={-1}
        className={className}
        onClick={(e) => {
          e.stopPropagation()
          setIsModalOpen(true)
          onDialogOpenChange?.(true)
        }}
        data-testid="button-share-connection"
      >
        <Share2 size={16} />
        <span className="sr-only">Share connection</span>
      </Button>

      <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
        <DialogContent
          onClick={(e) => e.stopPropagation()}
          className="p-4"
          onOverlayClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-1">
              <span>Share {app.name}</span>
              {getAccountIdentifier(app)}
            </DialogTitle>
          </DialogHeader>

          <div className="pt-3 flex flex-col gap-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1.5">
                <Typography variant="XS/500" className="text-secondary">
                  Share with Agents
                </Typography>
                <AgentSelect
                  value={form.values.users_with_access}
                  onChange={(values: string[]) => form.setFieldValue('users_with_access', values)}
                  placeholder="Search by name or email"
                  disabled={form.values.share_with_organization}
                  excludeIds={excludeIds}
                  initialValues={[]}
                />
                {form.errors.users_with_access && !form.values.share_with_organization && (
                  <Typography variant="XS/400" className="text-destructive">
                    {form.errors.users_with_access}
                  </Typography>
                )}
              </div>

              <div className="flex items-center">
                <Checkbox
                  name="share_with_organization"
                  value={form.values.share_with_organization}
                  onChange={(checked: boolean) =>
                    form.setFieldValue('share_with_organization', checked)
                  }
                  label=""
                />
                <Typography variant="P/500">Share with entire organization</Typography>
              </div>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center">
              <Typography variant="XS/500" className="text-secondary">
                Who has access
              </Typography>
            </div>

            <ScrollArea
              className={`mt-2 ${(accessRecords?.users.length ?? 0) + (accessRecords?.org ? 1 : 0) > 3 ? 'h-48' : 'h-32'}`}
            >
              <div className="space-y-2 pr-4">
                {isLoadingAccessRecords ? (
                  Array.from({ length: 3 }, (_, index) => `loading-skeleton-${index}`).map(
                    (skeletonId) => (
                      <div key={skeletonId} className="flex items-center gap-2 animate-pulse">
                        <div className="h-7 w-7 bg-muted rounded-full" />
                        <div className="flex-1 space-y-1">
                          <div className="h-4 bg-muted rounded w-3/4" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                        <div className="h-3 bg-muted rounded w-12" />
                      </div>
                    ),
                  )
                ) : (
                  <>
                    {/* Show integrated_by user first as Owner */}
                    {ownerId && (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-xxs-400 bg-bg3 text-foreground">
                            {getUserInitials(
                              userMap[ownerId]?.name || app.integrated_by?.name || '',
                              userMap[ownerId]?.email || app.integrated_by?.email || '',
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <Typography variant="S/400" className="text-foreground">
                            {titleize(userMap[ownerId]?.name ?? app.integrated_by?.name ?? '')}
                          </Typography>
                          <Typography variant="XS/400" className="text-secondary">
                            {userMap[ownerId]?.email ?? app.integrated_by?.email}
                          </Typography>
                        </div>
                        <Typography variant="XS/500" className="text-secondary">
                          Owner
                        </Typography>
                      </div>
                    )}

                    {/* Show other users with access */}
                    {accessRecords?.org && (
                      <div className="flex items-center gap-2 group hover:bg-hv1 -mx-2 px-2 py-1 rounded">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="text-xxs-400 bg-bg3 text-foreground">
                            ORG
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <Typography variant="S/400" className="text-foreground">
                            Entire organization
                          </Typography>
                          <Typography variant="XS/400" className="text-secondary">
                            Every agent in this organization
                          </Typography>
                        </div>
                        <Button
                          type="button"
                          variant="ghostDestructive"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRevokeOrganizationAccess()
                          }}
                          data-testid="button-revoke-organization-access"
                        >
                          Revoke
                        </Button>
                      </div>
                    )}

                    {/* Show other users with access */}
                    {/* One pass: flatMap both drops the owner and builds the row.
                        flatMap(...).map(...) was still two walks of the list. */}
                    {accessRecords?.users.flatMap((userId) =>
                      userId === ownerId ? (
                        []
                      ) : (
                        <div
                          key={userId}
                          className="flex items-center gap-2 group hover:bg-hv1 -mx-2 px-2 py-1 rounded"
                        >
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="text-xxs-400 bg-bg3 text-foreground">
                              {getUserInitials(
                                userMap[userId]?.name || '',
                                userMap[userId]?.email || '',
                              )}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <Typography variant="S/400" className="text-foreground">
                              {titleize(userMap[userId]?.name ?? '')}
                            </Typography>
                            <Typography variant="XS/400" className="text-secondary">
                              {userMap[userId]?.email}
                            </Typography>
                          </div>
                          <Button
                            type="button"
                            variant="ghostDestructive"
                            size="xs"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleRevokeAccess(userId)
                            }}
                            data-testid={`button-revoke-access-${userId}`}
                          >
                            Revoke
                          </Button>
                        </div>
                      ),
                    )}

                    {/* Show message if no users have access */}
                    {!ownerId &&
                      !accessRecords?.org &&
                      (accessRecords?.users.length ?? 0) === 0 && (
                        <Typography variant="S/400" className="text-secondary">
                          No users have access to this connection
                        </Typography>
                      )}
                  </>
                )}
              </div>
            </ScrollArea>
          </div>

          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                onOpenChange(false)
              }}
              className="h-9 px-4 py-1.5"
            >
              Cancel
            </Button>
            <ButtonWithLoader
              loading={isGrantAccessPending}
              disabled={!form.values.share_with_organization && !hasNewUsers}
              onClick={(e) => {
                e.stopPropagation()
                handleShare(form.values)
              }}
              className="h-9 px-4 py-1.5"
            >
              Share
            </ButtonWithLoader>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

const ShareConnectionButton = ({ app, className }: ShareConnectionButtonProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { details } = useAuth()
  const { data: users } = useUsersQuery()
  const { data: accessRecords, isLoading: isLoadingAccessRecords } = useAccessRecordsQuery(
    app.id || '',
    app.integration_source,
    isDialogOpen,
  )
  const { mutateAsync: grantAccess, isPending: isGrantAccessPending } = useGrantAccessMutation()
  const { mutate: revokeAccess } = useRevokeAccessMutation()

  return (
    <ShareConnectionButtonView
      app={app}
      className={className}
      currentUserId={details?.aom_uuid}
      accessRecords={accessRecords}
      users={users}
      isLoadingAccessRecords={isLoadingAccessRecords}
      isGrantAccessPending={isGrantAccessPending}
      onDialogOpenChange={setIsDialogOpen}
      onGrant={(payload) => grantAccess({ appId: app.id, payload, source: app.integration_source })}
      onRevoke={(payload) => {
        revokeAccess({ appId: app.id, payload, source: app.integration_source })
        return undefined
      }}
    />
  )
}

export default ShareConnectionButton
